import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { IEmployee } from '../../company/models/employee';
import { EmployeeService } from '../../company/services/employee.service';

export interface EmployeeState {
  availableHtecEmployeesForRevision: IEmployee[];
  htecEmployees: IEmployee[];
}

@Injectable()
export class EmployeeStore extends ComponentStore<EmployeeState> {

  // Initial state
  constructor(
    private employeeService: EmployeeService) {
      super({ 
        availableHtecEmployeesForRevision: [],
        htecEmployees: []
      });
  }

  // Select available employee for revision
  readonly availableHtecEmployeesForRevision$: Observable<IEmployee[]> = this.select(state => state.availableHtecEmployeesForRevision);

  // Select all htec employees
  readonly htecEmployees$: Observable<IEmployee[]> = this.select(state => state.htecEmployees);

  // Effect to load htec employees
  readonly getHtecEmployeesForRevision = this.effect<any>(request =>
    request.pipe(
      switchMap(request =>
        this.employeeService.getHtecEmployees().pipe(
          map(employees => {
            // Filter out employees that dont match
            const filteredEmployees = employees.data;

            const engineerIds = request.engineers.map(engineer => engineer.id);

            const finalFilteredEmployees = filteredEmployees.filter(employee =>
              !engineerIds.includes(employee.id)
            );
            
            return finalFilteredEmployees;
          }),
          tap((filteredEmployees) =>  {
            this.patchState({ availableHtecEmployeesForRevision: filteredEmployees })
          })
        )
      )
    )
  );

  // Effect to load all employee by company
  readonly getEmployeesByCompany = this.effect<string>(companyId$ =>
    companyId$.pipe(
      switchMap(companyId =>
        this.employeeService.getEmployees({pageNumber: 0, pageSize: 25, companyId: companyId}).pipe(
          tap((filteredEmployees) =>  {
            this.patchState({ htecEmployees: filteredEmployees.data })
          })
        )
      )
    )
  );

  // Method to add a device to availableDevicesForInstallation by its ID
  readonly addEmployeeByIdForRevision = this.effect<string>(employeeId$ =>
    employeeId$.pipe(
      switchMap(employeeId => 
        // Call the service to get the employee by ID
        this.employeeService.getEmployeeById(employeeId)
      ),
      tap((newEmployee: IEmployee) => {
        // Check if the device is already in the available devices
        const isEmployeeAlreadyPresent = this.get().availableHtecEmployeesForRevision.some(employee => employee.id === newEmployee.id);

        // Add the employee if it's not already present
        if (!isEmployeeAlreadyPresent) {
          this.patchState({
            availableHtecEmployeesForRevision: [...this.get().availableHtecEmployeesForRevision, newEmployee]
          });
        }
      })
    )
  );

  // Method to remove a employee from availableHtecEmployeesForRevision by its ID
  readonly removeEmployeeByIdForRevision = this.updater((state, employeeId: string) => {
    const updatedEmployees = state.availableHtecEmployeesForRevision.filter(employee => employee.id !== employeeId);
    return {
      ...state,
      availableHtecEmployeesForRevision: updatedEmployees
    };
  });

  readonly logStateChanges = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => {
        this.state$.subscribe(state => console.log('ComponentStore State:', state));
      })
    )
  );
}
